/**
 * The layer list of the Map Controls panel: a group header with eye + group
 * opacity slider, then indented layer rows with eye + swatch + opacity slider
 * + %.
 *
 * Visibility: a layer renders only when its group AND its own eye are on;
 * toggling a group off and back on restores each layer's own state (the
 * boundary eyes drive the app's own atoms). Opacity: a layer renders at
 * groupOpacity * layerOpacity, so both sliders always have an effect.
 *
 * Visual language (dark frosted glass rgba(0,62,103,0.6)): white / near-white
 * text and icons, light sliders, and a bright amber accent (#ffce54) for "on"
 * states so nothing is blue-on-blue.
 */
import React from 'react';
import { Box, IconButton, Slider, Tooltip, Typography } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useLayerState } from './useLayerState';
import type { LayerGroup, LayerLeaf } from './layerRegistry';
import { ACCENT, TEXT, TEXT_DIM, WHITE } from './palette';
import { panelSliderSx } from './panelFields';

const SLIDER_WIDTH = 84;
/** Width of the % readout; OpacityHeader mirrors it so the caption sits
 * exactly over the slider column. */
const PCT_WIDTH = 36;

type LayerState = ReturnType<typeof useLayerState>;

const Swatch: React.FC<{ color: string }> = ({ color }) => (
  <Box
    aria-hidden
    sx={{
      width: 12,
      height: 12,
      flex: '0 0 12px',
      borderRadius: '2px',
      backgroundColor: color,
      border: '1px solid rgba(255,255,255,0.7)',
    }}
  />
);

const Pct: React.FC<{ value: number }> = ({ value }) => (
  <Typography
    variant="caption"
    sx={{ width: PCT_WIDTH, textAlign: 'right', color: WHITE, fontVariantNumeric: 'tabular-nums' }}
  >
    {Math.round(value * 100)}%
  </Typography>
);

const EyeButton: React.FC<{ visible: boolean; label: string; onClick: () => void }> = ({
  visible,
  label,
  onClick,
}) => (
  <Tooltip title={visible ? `Hide ${label}` : `Show ${label}`}>
    <IconButton
      size="small"
      aria-label={`${visible ? 'Hide' : 'Show'} ${label}`}
      onClick={onClick}
      sx={{
        color: visible ? WHITE : 'rgba(255,255,255,0.45)',
        '&:focus-visible': { outline: `2px solid ${ACCENT}` },
      }}
    >
      {visible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
    </IconButton>
  </Tooltip>
);

/** Group header: the group's own eye + name + group opacity slider. */
const GroupHeader: React.FC<{ group: LayerGroup; state: LayerState }> = ({ group, state }) => {
  const value = state.groupOpacityValue(group.key);
  const visible = state.isGroupVisible(group.key);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 32 }}>
      <EyeButton
        visible={visible}
        label={`all ${group.label} layers`}
        onClick={() => state.toggleGroupVisible(group.key)}
      />
      <Typography
        variant="subtitle2"
        sx={{ flex: 1, fontWeight: 700, fontSize: 13, color: TEXT, letterSpacing: 0.2 }}
      >
        {group.label}
      </Typography>
      <Slider
        size="small"
        value={Math.round(value * 100)}
        min={0}
        max={100}
        aria-label={`Group opacity for ${group.label}`}
        sx={{ ...panelSliderSx, width: SLIDER_WIDTH }}
        onChange={(_e, v) => !Array.isArray(v) && state.setGroupOpacityValue(group.key, v / 100)}
      />
      <Pct value={value} />
    </Box>
  );
};

/** A right-aligned "Opacity" caption that sits directly over the slider column
 * so users know what the sliders control. Mirrors a row's trailing structure
 * (slider + % readout widths) so it lines up exactly. */
const OpacityHeader: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
    <Box sx={{ flex: 1 }} />
    <Typography
      variant="caption"
      sx={{
        width: SLIDER_WIDTH,
        textAlign: 'center',
        color: TEXT_DIM,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
      }}
    >
      Opacity
    </Typography>
    <Box sx={{ width: PCT_WIDTH }} />
  </Box>
);

const LeafRow: React.FC<{ leaf: LayerLeaf; group: LayerGroup; state: LayerState }> = ({
  leaf,
  group,
  state,
}) => {
  const visible = state.isLeafVisible(leaf.key);
  const shown = state.isLeafShown(leaf.key, group.key);
  const value = state.leafOpacityValue(leaf.key);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        pl: 2,
        minHeight: 30,
        // The whole row dims while its group is off: the layer won't render
        // regardless of its own eye, which stays interactive and remembered.
        opacity: state.isGroupVisible(group.key) ? 1 : 0.5,
      }}
    >
      <EyeButton
        visible={visible}
        label={leaf.label}
        onClick={() => state.toggleLeafVisible(leaf.key)}
      />
      <Swatch color={leaf.swatch} />
      <Typography
        variant="body2"
        sx={{ flex: 1, fontSize: 12.5, color: shown ? TEXT : 'rgba(255,255,255,0.5)' }}
      >
        {leaf.label}
      </Typography>
      <Slider
        size="small"
        value={Math.round(value * 100)}
        min={0}
        max={100}
        aria-label={`Opacity for ${leaf.label}`}
        sx={{ ...panelSliderSx, width: SLIDER_WIDTH }}
        onChange={(_e, v) => !Array.isArray(v) && state.setLeafOpacityValue(leaf.key, v / 100)}
      />
      <Pct value={value} />
    </Box>
  );
};

export const LayerControls: React.FC = () => {
  const state = useLayerState();

  if (state.presentGroups.length === 0) {
    return (
      <Typography variant="caption" sx={{ color: TEXT_DIM, pointerEvents: 'auto' }}>
        No layers on the map yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ pointerEvents: 'auto' }}>
      <OpacityHeader />
      {state.presentGroups.map(group => (
        <Box key={group.key} sx={{ mb: 0.5 }}>
          <GroupHeader group={group} state={state} />
          {group.children.map(leaf => (
            <LeafRow key={leaf.key} leaf={leaf} group={group} state={state} />
          ))}
        </Box>
      ))}
    </Box>
  );
};
